import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useRazorpay } from "react-razorpay";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Minus, Plus, Trash2, ShoppingBag, ShieldCheck } from "lucide-react";
import { getCart, setQty, saveCart, clearCart } from "@/lib/cart";
import { getConfig } from "@/lib/config";
import { apiFetch } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const EASE = [0.22, 1, 0.36, 1];

export default function Cart() {
  const [items, setItems] = useState(getCart());
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);
  const [paidOrderId, setPaidOrderId] = useState("");
  const [checkoutOn, setCheckoutOn] = useState(false);
  const [paypalCurrency, setPaypalCurrency] = useState("INR");
  const { Razorpay } = useRazorpay();

  useEffect(() => {
    getConfig().then((c) => {
      setCheckoutOn(!!c.checkout_enabled);
      if (c.paypal_currency) setPaypalCurrency(c.paypal_currency);
    });
  }, []);

  useEffect(() => {
    const sync = () => setItems(getCart());
    window.addEventListener("lw-cart", sync);
    return () => window.removeEventListener("lw-cart", sync);
  }, []);

  const total = items.reduce((s, i) => s + (i.price || 0) * i.qty, 0);
  const unpriced = items.filter((i) => i.price == null);

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const order = await apiFetch("/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
          name,
          contact,
        }),
      });
      const rzp = new Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: "INR",
        name: "Laxwedeepan",
        description: "Sacred offerings, made with bhakti",
        order_id: order.order_id,
        prefill: { name, contact },
        theme: { color: "#7A1F1E" },
        handler: async (response) => {
          try {
            await apiFetch("/api/orders/verify", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(response),
            });
            clearCart();
            setPaidOrderId(order.order_id);
            setPaid(true);
          } catch {
            setError("We couldn't confirm your payment yet. If money was deducted, your order is safe. We'll confirm it shortly, or WhatsApp us at +91 92055 11502.");
          }
        },
      });
      rzp.on("payment.failed", () => setError("The payment did not complete. Please try again."));
      rzp.open();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <main className="mx-auto max-w-xl px-6 pt-44 pb-32 text-center" data-testid="cart-success">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <ShieldCheck className="mx-auto h-12 w-12 text-brand-gold" strokeWidth={1.25} />
          <h1 className="mt-6 font-display text-4xl font-medium text-brand-ink md:text-5xl">Order received, <em className="italic text-brand-maroon">with gratitude.</em></h1>
          <p className="mt-6 text-base font-light leading-relaxed text-brand-ink/70">
            Your payment is confirmed and your offerings are being prepared with care.
            We will reach you shortly on your contact number with delivery details.
          </p>
          {paidOrderId && (
            <p className="mt-4 text-sm font-medium tracking-wide text-brand-maroon" data-testid="cart-order-number">
              Order number: {paidOrderId}
            </p>
          )}
          <Link to="/collection" data-testid="cart-success-continue" className="mt-10 inline-block rounded-full bg-brand-maroon px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory hover:bg-brand-gold">
            Continue Browsing
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-6 pt-36 pb-28 lg:px-12" data-testid="cart-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
        <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">Your Selection</p>
        <h1 className="mt-4 font-display text-4xl font-medium text-brand-ink md:text-5xl">The Cart</h1>

        {items.length === 0 ? (
          <div className="mt-16 text-center">
            <ShoppingBag className="mx-auto h-12 w-12 text-brand-border" strokeWidth={1.25} />
            <p className="mt-6 font-display text-2xl italic text-brand-ink/60">Your cart awaits its first offering.</p>
            <Link to="/collection" data-testid="cart-empty-browse" className="mt-8 inline-block rounded-full bg-brand-maroon px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory hover:bg-brand-gold">
              Explore the Collection
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="space-y-5 md:col-span-7">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-5 rounded-sm border border-brand-border bg-white p-4" data-testid={`cart-item-${i.id}`}>
                  {i.img && <img src={i.img} alt={i.name} className="h-20 w-16 rounded-sm object-cover" />}
                  <div className="flex-1">
                    <p className="font-display text-xl text-brand-ink">{i.name}</p>
                    {i.price != null && <p className="text-sm text-brand-maroon">₹{i.price}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQty(i.id, i.qty - 1)} aria-label="Decrease quantity" data-testid={`cart-minus-${i.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-border text-brand-ink/60 hover:border-brand-gold">
                      <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <span className="w-6 text-center text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                    <button onClick={() => setQty(i.id, i.qty + 1)} disabled={i.stock != null && i.qty >= i.stock} title={i.stock != null && i.qty >= i.stock ? `Only ${i.stock} available` : undefined} aria-label="Increase quantity" data-testid={`cart-plus-${i.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-border text-brand-ink/60 hover:border-brand-gold disabled:cursor-not-allowed disabled:opacity-40">
                      <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                  <button
                    onClick={() => saveCart(getCart().filter((x) => x.id !== i.id))}
                    aria-label="Remove item"
                    data-testid={`cart-remove-${i.id}`}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-brand-border text-brand-ink/40 hover:border-brand-maroon hover:text-brand-maroon"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
            <div className="md:col-span-5">
              <div className="rounded-sm border border-brand-border bg-white p-7">
                <h2 className="font-display text-2xl font-medium text-brand-ink">Summary</h2>
                <div className="mt-5 flex justify-between border-t border-brand-border pt-5">
                  <span className="text-sm font-light text-brand-ink/60">Total</span>
                  <span className="font-display text-2xl text-brand-maroon" data-testid="cart-total">₹{total}</span>
                </div>
                {unpriced.length > 0 && (
                  <p className="mt-4 text-xs font-light leading-relaxed text-brand-maroon" data-testid="cart-unpriced-note">
                    {unpriced.map((u) => u.name).join(", ")} {unpriced.length > 1 ? "are" : "is"} priced on enquiry — remove to pay online, or WhatsApp us.
                  </p>
                )}
                <div className="mt-6 space-y-4">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="cart-name-input" className="w-full rounded-sm border border-brand-border bg-white px-4 py-2.5 text-sm font-light outline-none focus:border-brand-gold" />
                  <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Phone / WhatsApp number" data-testid="cart-contact-input" className="w-full rounded-sm border border-brand-border bg-white px-4 py-2.5 text-sm font-light outline-none focus:border-brand-gold" />
                </div>
                {error && <p className="mt-4 text-sm text-brand-maroon" data-testid="cart-error">{error}</p>}
                {checkoutOn ? (
                  <>
                    <button
                      onClick={pay}
                      disabled={busy || unpriced.length > 0 || items.length === 0}
                      data-testid="cart-pay-button"
                      className="mt-6 w-full rounded-full bg-brand-maroon py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy ? "Opening Razorpay…" : `Pay ₹${total} Securely`}
                    </button>
                    {PAYPAL_CLIENT_ID && (
                      <div className="mt-5" data-testid="cart-paypal-section">
                        <p className="mb-3 text-center text-[10px] uppercase tracking-[0.2em] text-brand-ink/40">or pay with</p>
                        <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: paypalCurrency }}>
                          <PayPalButtons
                            style={{ layout: "vertical", shape: "pill", height: 45 }}
                            disabled={busy || unpriced.length > 0 || items.length === 0}
                            forceReRender={[total, paypalCurrency]}
                            createOrder={async () => {
                              setError("");
                              try {
                                const order = await apiFetch("/api/paypal/orders", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({
                                    items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
                                    name,
                                    contact,
                                  }),
                                });
                                return order.order_id;
                              } catch (err) {
                                setError(err.message);
                                throw err;
                              }
                            }}
                            onApprove={async (data) => {
                              try {
                                await apiFetch(`/api/paypal/orders/${data.orderID}/capture`, { method: "POST" });
                                clearCart();
                                setPaidOrderId(data.orderID);
                                setPaid(true);
                              } catch {
                                setError("We couldn't confirm your payment yet. If money was deducted, your order is safe. We'll confirm it shortly, or WhatsApp us at +91 92055 11502.");
                              }
                            }}
                            onError={() => setError("PayPal could not be loaded right now. Please try Razorpay above, or order on WhatsApp.")}
                          />
                        </PayPalScriptProvider>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mt-6 text-sm font-light leading-relaxed text-brand-ink/60" data-testid="cart-checkout-soon">
                      Online checkout is coming soon — please order on WhatsApp and we will confirm everything personally.
                    </p>
                    <a
                      href={`https://wa.me/919205511502?text=${encodeURIComponent(`Namaste Laxwedeepan, I would like to order: ${items.map((i) => `${i.name} x${i.qty}`).join(", ")}${total ? ` (total ₹${total})` : ""}.`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="cart-whatsapp-order"
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand-maroon py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold"
                    >
                      <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
                      Order on WhatsApp
                    </a>
                  </>
                )}
                {checkoutOn && (
                  <p className="mt-4 flex items-center justify-center gap-2" data-testid="cart-razorpay-badge">
                    <ShieldCheck className="h-3.5 w-3.5 text-brand-gold" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-brand-ink/40">Secured payments by</span>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/8/89/Razorpay_logo.svg" alt="Razorpay" className="h-3.5 w-auto opacity-80" />
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </main>
  );
}
